import {
  Eye,
  EyeOff,
  LogIn,
  Mail,
  PartyPopper,
  Unlock,
} from "@tamagui/lucide-icons";
import { observer } from "mobx-react-lite";
import React from "react";
import { Pressable } from "react-native";
import { type SheetProps, XStack, YStack } from "tamagui";
import { AccountSheetActive } from "../../plans/PlanModel";
import { Body } from "../../ui/Body";
import { Button } from "../../ui/Button";
import { Input } from "../../ui/InputNew";
import { Pill, PillType } from "../../ui/Pill";
import { Sheet } from "../../ui/Sheet";
import { Subtext } from "../../ui/Subtext";
import { Title } from "../../ui/Title";
import { accountSignIn } from "../firebaseInit";
import { trackEvent } from "../trackEvent";

export const AccountLoginSheet = observer(
  ({
    open,
    onChangeSheet,
    onLoginDone,
    ...props
  }: {
    onLoginDone?: () => void;
    onChangeSheet?: (sheet: AccountSheetActive) => void;
  } & SheetProps) => {
    const [email, setEmail] = React.useState("");
    const [password, setPassword] = React.useState("");
    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<null | Error>(null);

    const [showingPassword, setShowingPassword] = React.useState(false);

    const login = React.useCallback(async () => {
      const e = email.trim();
      if (e) {
        setError(null);
        setLoading(true);
        try {
          await accountSignIn(e, password);
          trackEvent("account_login");

          onLoginDone?.();
        } catch (e: unknown) {
          if (e instanceof Error) setError(e);
          trackEvent("account_login_failed");
        } finally {
          setLoading(false);
        }
      }
    }, [email, onLoginDone, password]);

    return (
      <Sheet
        isLoading={loading}
        sheetId="account_sign_in"
        open={open}
        onOpenChange={() => {
          onChangeSheet?.(AccountSheetActive.None);
        }}
        {...props}
        header={
          <YStack gap="$space.medium">
            <XStack gap="$medium" alignItems="center">
              <LogIn />
              <Title>Welcome back!</Title>
            </XStack>
            <Subtext>Login to access your Planiras on any device.</Subtext>
          </YStack>
        }
      >
        <YStack gap="$space.medium">
          {error && <Pill type={PillType.Danger}>{error.message}</Pill>}

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Mail />
            <Input
              disabled={loading}
              autoComplete={"email"}
              keyboardType={"email-address"}
              inputMode={"email"}
              textContentType={"username"}
              preventNewLine
              defaultValue={""}
              multiline={false}
              type="border"
              placeholder={"Your email..."}
              onChangeText={(text) => {
                setEmail(text);
              }}
            />
          </XStack>

          <XStack width={"100%"} alignItems="center" gap="$space.medium">
            <Unlock />
            <Input
              disabled={loading}
              secureTextEntry={!showingPassword}
              preventNewLine
              textContentType={"password"}
              defaultValue={""}
              autoComplete="current-password"
              autoCorrect={false}
              multiline={false}
              icon={showingPassword ? Eye : EyeOff}
              onIconPress={() => {
                setShowingPassword(!showingPassword);
              }}
              type="border"
              placeholder={"Your password..."}
              onChangeText={(text) => {
                setPassword(text);
              }}
              onSubmitEditing={login}
            />
          </XStack>

          <XStack style={{ justifyContent: "space-between" }}>
            <Pressable
              onPress={() => {
                onChangeSheet?.(AccountSheetActive.Register);
              }}
            >
              <Body muted>No account?</Body>
            </Pressable>
            <Pressable
              onPress={() => {
                onChangeSheet?.(AccountSheetActive.ForgotPassword);
              }}
            >
              <Body muted>Forgot password?</Body>
            </Pressable>
          </XStack>

          <Button isLoading={loading} icon={PartyPopper} onPress={login}>
            Let me in!
          </Button>
        </YStack>
      </Sheet>
    );
  },
);
